import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPhaseData } from '../../data/programData';
import { 
  Dumbbell, 
  ClipboardCheck, 
  TrendingUp, 
  Lock,
  ChevronRight,
  KeyRound,
  Play
} from 'lucide-react';
import { Button, Input } from '../../components/ui';
import { api } from '../../services/api';

export function PhaseOverviewPage() {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const { aclCare, updateAclCare, user } = useAuth();
  
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [unlockCode, setUnlockCode] = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError, setUnlockError] = useState('');

  const phaseNum = parseInt(phaseId);
  const phaseData = getPhaseData(phaseNum);
  const phaseKey = `phase_${phaseNum}`;
  const userPhaseData = aclCare?.phases?.[phaseKey];
  
  const isUnlocked = !user?.patient_id || userPhaseData?.unlocked;
  const isTestOutPassed = userPhaseData?.test_out_passed;
  const previousPhaseKey = phaseNum > 1 ? `phase_${phaseNum - 1}` : null;
  const previousPhasePassed = previousPhaseKey ? aclCare?.phases?.[previousPhaseKey]?.test_out_passed : true;

  // For phases 2-4, check if previous phase test out is passed
  const canAccess = phaseNum === 1 || isUnlocked || previousPhasePassed;

  const handleUnlockSubmit = async (e) => {
    e.preventDefault();
    setUnlockLoading(true);
    setUnlockError('');

    try {
      const result = await api.validateUnlockCode(unlockCode, phaseNum);
      
      if (result.valid) {
        await updateAclCare({
          ...aclCare,
          phases: {
            ...aclCare.phases,
            [phaseKey]: {
              ...aclCare.phases[phaseKey],
              unlocked: true,
              unlocked_at: new Date().toISOString(),
              unlocked_method: 'code',
            }
          },
          unlock_codes_used: [
            ...(aclCare.unlock_codes_used || []),
            {
              code: unlockCode,
              phase: phaseNum,
              used_at: new Date().toISOString(),
              provided_by: null
            }
          ]
        });
        setShowUnlockModal(false);
        setUnlockCode('');
      } else {
        setUnlockError('Invalid unlock code. Please try again.');
      }
    } catch (err) {
      setUnlockError(err.message || 'Failed to validate code');
    } finally {
      setUnlockLoading(false);
    }
  };

  if (!phaseData) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <p className="text-gray-500">Phase not found</p>
      </div>
    );
  }

  const menuItems = [
    {
      id: 'training',
      label: 'Training Sessions',
      description: 'View and complete workouts',
      icon: Dumbbell,
      path: `/program/phase/${phaseId}/training`,
      color: 'blue',
    },
    {
      id: 'test-out',
      label: 'Test Out',
      description: isTestOutPassed ? 'Completed' : 'Assess your progress',
      icon: ClipboardCheck,
      path: `/program/phase/${phaseId}/test-out`,
      color: 'green',
      badge: isTestOutPassed ? 'Passed' : null,
    },
    {
      id: 'progress',
      label: 'My Progress',
      description: 'Track your phase progress',
      icon: TrendingUp,
      path: `/program/phase/${phaseId}/progress`,
      color: 'purple',
    },
  ];

  // Add Return to Run for Phase 3
  if (phaseData.hasReturnToRun) {
    menuItems.push({
      id: 'return-to-run',
      label: 'Return to Run Program',
      description: 'Running progression guide',
      icon: Play,
      path: `/program/phase/${phaseId}/return-to-run`,
      color: 'orange',
    });
  }

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <div className="max-w-2xl mx-auto p-4 pb-24 lg:pb-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">{phaseData.title}</h1>
      {/* Removing subtitle for now
      <p className="text-gray-500 mb-6">{phaseData.subtitle}</p>
      */}
      {/* Phase Video */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
        <div className="aspect-video bg-gray-900">
          <iframe
            src={phaseData.videoUrl}
            title={`${phaseData.title} Overview`}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>

      {/* Locked State */}
      {!canAccess && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6">
          <div className="flex items-start">
            <Lock className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <h3 className="font-medium text-yellow-800">Phase Locked</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Complete the Phase {phaseNum - 1} test out to unlock this phase, or enter an unlock code.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowUnlockModal(true)}
                className="mt-3"
              >
                <KeyRound className="h-4 w-4 mr-2" />
                Enter Unlock Code
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Menu Items */}
      {canAccess && (
        <div className="space-y-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className="w-full bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center justify-between hover:shadow-md transition-shadow"
              >
                <div className="flex items-center">
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center mr-4 ${colorClasses[item.color]}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="text-left">
                    <div className="flex items-center">
                      <span className="font-semibold text-gray-900">{item.label}</span>
                      {item.badge && (
                        <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </button>
            );
          })}
        </div>
      )}

      {/* Unlock Code Modal */}
      {showUnlockModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div 
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowUnlockModal(false)}
          />
          
          <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
            <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Enter Unlock Code</h2>
              
              <form onSubmit={handleUnlockSubmit}>
                <Input
                  label="Unlock Code"
                  value={unlockCode}
                  onChange={(e) => setUnlockCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  error={unlockError}
                  autoFocus
                />
                
                <div className="flex gap-3 mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowUnlockModal(false)}
                    fullWidth
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    loading={unlockLoading}
                    disabled={!unlockCode}
                    fullWidth
                  >
                    Unlock Phase
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}